<template>
  <v-row class="ma-0" dense v-if="localValue">
    <v-col :cols="productableOptions.length ? 4 : 9" class="py-0">
      <v-autocomplete
        v-model="localValue.item_id"
        :items="productOptions"
        :loading="loadingProducts"
        item-title="label"
        item-value="value"
        label="Servicio"
        density="compact"
        @update:modelValue="onProductSelect"
        @update:search="onSearch"
        @focus="onMenuFocus"
        chips
        variant="solo-filled">
        <template v-slot:chip="{ props, item }">
          <div v-bind="props" style="display: flex; align-items: center; gap: 4px; padding: 0 6px; min-width: 0;">
            <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
              {{ productOptions.find(product => product.value == item.value)?.raw?.attributes?.description || item.label }}
            </span>
            <v-chip size="x-small" :color="productOptions.find(product => product.value == item.value)?.raw?.attributes?.organization?.color" label style="font-size: 10px; height: 15px;" class="ml-2">{{productOptions.find(product => product.value == item.value)?.raw?.attributes?.organization?.name}}</v-chip>
            <v-chip v-if="Number(localValue.unit_price) === 0" size="x-small" color="green" label style="font-size: 10px; height: 15px;" class="ml-2">Cortesía</v-chip>
          </div>
        </template>
        <template v-slot:item="{ item, props }">
          <v-row class="ma-0 pa-4" v-bind="props" style="cursor: pointer;">
            <v-list-item-title>
              {{ item?.raw?.raw?.attributes?.description || item?.title || item }}
            </v-list-item-title>
            <v-spacer/>
            <v-chip size="x-small" v-if="item?.raw?.raw?.attributes?.organization != null">
              {{ item?.raw?.raw?.attributes?.organization?.name || '' }}
            </v-chip>
          </v-row>
        </template>
      </v-autocomplete>
    </v-col>

    <v-col :cols="5" v-if="productableOptions.length" class="py-0">
      <v-autocomplete
        variant="solo-filled"
        v-model="localValue.productable_id"
        :items="productableOptions"
        item-title="label"
        item-value="value"
        label="Seleccionar"
        density="compact"
        @update:modelValue="onProductableSelect"
      />
    </v-col>

    <v-col :cols="3" class="py-0">
      <v-text-field
        v-model="localValue.unit_price"
        prefix="$"
        type="number"
        label="Precio"
        density="compact"
        :hint="requiereAutorizacion ? `Requiere autorización` : []"
        variant="solo-filled"
      />
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import { useProductsStore } from '@/store/products';
import { useClientsStore } from '@/store/clients';
import { debounce } from 'lodash';

const cachedSearches = new Map<string, any[]>();

const emit = defineEmits(['update:modelValue', 'validation']);
const props = defineProps({
  modelValue: { type: Object, required: true },
  clientId: { type: [String, Number], required: false },
  subject: { type: Object, required: false }
});

const clientStore = useClientsStore();
const productsStore = useProductsStore();

const localValue = ref({ ...props.modelValue });
const productOptions = ref<any[]>([]);
const productableOptions = ref<any[]>([]);
const loadingProducts = ref(false);

const validate = () => {
  const valid =
    !!localValue.value.item_id &&
    (!!localValue.value.unit_price || localValue.value.unit_price === 0) &&
    (productableOptions.value.length === 0 || !!localValue.value.productable_id);

  emit('validation', valid);
};

watch(() => props.modelValue, (val) => {
  localValue.value = { ...val };
  hydrateSelectedProduct(val);
}, { deep: true });

watch(() => localValue.value, (val) => {
  emit('update:modelValue', val);
  validate();
}, { deep: true });

const onProductSelect = async (productId: number) => {
  const selected = productOptions.value.find(p => p.value === productId);
  if (!selected) return;

  localValue.value.item_id = productId;

  const productables = selected.raw?.attributes?.productable || [];

  // LIMPIA siempre antes de modificar
  localValue.value.productable_id = null;
  localValue.value.productables = [];

  if (productables.length > 0) {
    productableOptions.value = productables.map((p: any) => ({
      value: p.id,
      label: `${p.attributes.sku || ''} | ${p.attributes.address || ''}`.trim(),
      raw: p
    }));
    localValue.value.productables = productables;
  } else {
    productableOptions.value = [];
    // Settea directos para producto plano
    localValue.value.productable_id = productId;
    localValue.value.productable_type = selected.raw?.attributes?.productable_type;
    await fetchPrice({
      productId,
      productType: selected.raw?.attributes?.productable_type
    });
  }
};


const onProductableSelect = async (productableId: number) => {
  const product = localValue.value.productables?.find((p: any) => p.id === productableId);
  if (!product) return;

  localValue.value.productable_id = productableId;
  localValue.value.productable_type = product.attributes.productable_type;

  await fetchPrice({
    productId: productableId,
    productType: product.attributes.productable_type,
  });
};

const suggestedPrice = ref<number|null>(null);

const fetchPrice = async ({
  productId,
  productType
}: {
  productId: number;
  productType: string;
}) => {

  const subject = props.subject;
  const subjectId = subject?.id;
  const clientTypeId = subject?.attributes?.client_type?.id;

  if (!subjectId || !clientTypeId || !productId || !productType) return;

  try {
    const price = await productsStore.fetchSuggestedPrice(
      'filter[client_type_id]=' + clientTypeId +
      '&filter[product_type]=' + productType +
      '&filter[product_id]=' + productId
    );
    localValue.value.unit_price = price;
    suggestedPrice.value = price; // <-- Aquí guardas el precio sugerido
  } catch (err) {
    console.error('Error fetching price:', err);
    suggestedPrice.value = null;
  }
};

const requiereAutorizacion = computed(() =>
  suggestedPrice.value != null &&
  Number(localValue.value.unit_price) < Number(suggestedPrice.value)
);

const onMenuFocus = () => {
  // Solo busca si NO hay opciones
  if (!productOptions.value.length) {
    onSearch('');
  }
};

const onSearch = debounce(async (search: string) => {
  if (typeof search !== 'string') return; // Solo busca si es texto
  if (!search && productOptions.value.length) return; // Si ya hay opciones, no busques otra vez
  //if (!search || search.length < 2) return;
  loadingProducts.value = true;

  if (cachedSearches.has(search)) {
    productOptions.value = cachedSearches.get(search)!;
    loadingProducts.value = false;
    return;
  }

  try {
    const { data } = await productsStore.fetchProducts(typeof search === 'string'?{ 'filter[description]': search }:'');
    const mapped = mapProducts(data);
    cachedSearches.set(search, mapped);
    productOptions.value = mapped;
  } catch (error) {
    console.error('Error searching products:', error);
  } finally {
    loadingProducts.value = false;
  }
}, 300);

const mapProducts = (data: any[]) =>
  data.map(p => ({
    value: p.id,
    label: p.attributes.description,
    raw: p
  }));

const hydrateSelectedProduct = (value: any) => {
  const { item_id, productables, productable_type, product_description, product_organization } = value || {};

  // Si es productable y tiene product base
  if (productable_type !== 'Product' && productables?.length) {
    const product = productables[0]?.attributes?.product;
    if (product) {
      productOptions.value = [{
        value: item_id,
        label: product.attributes?.description || 'Producto',
        raw: product
      }];
    }
    productableOptions.value = productables.map((p: any) => ({
      value: p.id,
      label: `${p.attributes.sku || ''} | ${p.attributes.address || ''}`.trim(),
      raw: p
    }));
  }

  // **Si es producto directo (tipo Product plano, sin productables)**
  if (productable_type === 'Product' && item_id) {
    // **Nuevo: Si productOptions está vacío, lo llenas con el item**
    if (!productOptions.value.length) {
      productOptions.value = [{
        value: item_id,
        label: product_description || 'Producto',
        raw: { id: item_id, attributes: { description: product_description, organization: product_organization } }
      }];
    }
  }
};

onMounted(() => {
  hydrateSelectedProduct(localValue.value);
});


watch(productableOptions, (opts) => {
  if (!opts.length) {
    localValue.value.productable_id = null;
  }
});
</script>
